import { ANALYSIS_VERSIONS } from "../variables/analysisVersions";

export const STOCK_CYCLE_DATA_SERVICE = {
    normalizeV2Content,
    normalizeV3Content,
    isV3,
    getOutlierOrders,
    isOutlierOrder,
    isOutlierDetectionSkipped,
    isOutlierWarning
}

/**
 * V2 응답에 버전 표시만 붙인다. 화면이 참조하는 iqrData 는 서버가 이미 채워서 내려준다.
 */
function normalizeV2Content(content) {
    if (!content) {
        return content;
    }

    return {
        ...content,
        analysisVersion: ANALYSIS_VERSIONS.V2,
        productOptionsDetailList: content?.productOptionsDetailList?.map(productOptionDetail => {
            return {
                ...productOptionDetail,
                analysisVersion: ANALYSIS_VERSIONS.V2
            }
        })
    }
}

/**
 * V3 응답을 V2 화면이 그대로 읽을 수 있는 형태로 맞춘다.
 *
 * V3 는 iqrData 를 항상 null 로 내려주고 보정값을 adjustedData 에 담기 때문에,
 * 화면 전반이 참조하는 iqrData 자리에 adjustedData 를 매핑해 둔다.
 * adjustedData 원본은 그대로 남겨두어 V3 전용 UI(이상 주문건 목록 등)에서 사용한다.
 */
function normalizeV3Content(content) {
    if (!content) {
        return content;
    }

    const outlierPolicy = content?.outlierPolicy;

    return {
        ...content,
        analysisVersion: ANALYSIS_VERSIONS.V3,
        productOptionsDetailList: content?.productOptionsDetailList?.map(productOptionDetail => {
            const adjustedData = productOptionDetail?.adjustedData;

            return {
                ...productOptionDetail,
                analysisVersion: ANALYSIS_VERSIONS.V3,
                outlierPolicy: outlierPolicy,
                iqrData: adjustedData ? {
                    releaseQty: adjustedData?.releaseQty,
                    releaseQtyPerDay: adjustedData?.releaseQtyPerDay,
                    // V3 의 상한. V2 의 upperBound 와 같은 자리에서 쓰인다.
                    upperBound: adjustedData?.outlierBound,
                    // V3 는 하한 판정을 하지 않는다.
                    lowerBound: null
                } : null
            }
        })
    }
}

function isV3(productOptionDetail) {
    return productOptionDetail?.analysisVersion === ANALYSIS_VERSIONS.V3;
}

/**
 * 총량에서 제외된 주문건 목록. V3 는 서버가 내려준 목록을 그대로 쓴다.
 */
function getOutlierOrders(productOptionDetail) {
    if (!isV3(productOptionDetail)) {
        return null;
    }
    return productOptionDetail?.adjustedData?.outlierOrders || [];
}

function isOutlierOrder(productOptionDetail, order) {
    const outlierOrders = getOutlierOrders(productOptionDetail);

    if (outlierOrders) {
        return outlierOrders.some(outlierOrder => outlierOrder?.erpItemId === order?.erpItemId);
    }

    const iqrData = productOptionDetail?.iqrData;
    return order?.releaseQty < iqrData?.lowerBound || order?.releaseQty > iqrData?.upperBound;
}

/**
 * V3 에서 outlierCount 가 0 인 경우는 "이상치가 없었다" 와 "표본이 부족해 판정하지 않았다" 두 가지다.
 */
function isOutlierDetectionSkipped(productOptionDetail) {
    if (!isV3(productOptionDetail)) {
        return false;
    }

    const orderCount = productOptionDetail?.adjustedData?.orderCount;
    const minOrderCount = productOptionDetail?.outlierPolicy?.minOrderCountForDetection;

    if (orderCount === undefined || orderCount === null || !minOrderCount) {
        return false;
    }

    return orderCount < minOrderCount;
}

/**
 * 행에 이상치 경고를 띄울지 여부.
 * V3 는 서버가 실제로 제외한 주문건이 있는지를 그대로 쓰고,
 * V2 는 보정 후 일평균이 원본 대비 70% 미만으로 줄었는지로 판단한다.
 */
function isOutlierWarning(productOptionDetail) {
    if (isV3(productOptionDetail)) {
        return productOptionDetail?.adjustedData?.outlierCount > 0;
    }

    return ((productOptionDetail?.iqrData?.releaseQtyPerDay / productOptionDetail?.releaseQtyPerDay) * 100) < 70;
}
